import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, Trash, Key as KeyIcon, Lock, LockOpen } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useConfirm } from '../components/ConfirmProvider';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Users = () => {
  const confirm = useConfirm();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'seller',
    full_name: ''
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (e) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
      toast.success('Utilisateur créé');
      setShowDialog(false);
      setFormData({ username: '', password: '', role: 'seller', full_name: '' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const openPasswordDialog = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error('Le mot de passe doit contenir au moins 4 caractères');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/users/${editingUser.id}/password`,
        { new_password: newPassword }, { withCredentials: true });
      toast.success('Mot de passe modifié');
      setShowPasswordDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const toggleBlock = async (user) => {
    const action = user.is_blocked ? 'débloquer' : 'bloquer';
    if (!(await confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} cet utilisateur ?`,
      description: user.is_blocked
        ? `${user.username} pourra à nouveau se connecter à l'application.`
        : `${user.username} ne pourra plus se connecter tant que vous ne l'aurez pas débloqué.`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      destructive: !user.is_blocked,
    }))) return;
    try {
      const r = await axios.put(`${API_URL}/api/users/${user.id}/block`, {}, { withCredentials: true });
      toast.success(r.data.message);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (user) => {
    if (!(await confirm({
      title: 'Supprimer cet utilisateur ?',
      description: `${user.username} sera supprimé définitivement. Cette action est irréversible.`,
      confirmText: 'Supprimer',
    }))) return;
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}`, { withCredentials: true });
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="users-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Utilisateurs</h1>
            <p className="text-slate-600">Gérez les comptes administrateur et vendeur</p>
          </div>
          <Button
            onClick={() => { setFormData({ username: '', password: '', role: 'seller', full_name: '' }); setShowDialog(true); }}
            data-testid="create-user-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Créer un utilisateur
          </Button>
        </div>

        {loading ? <div className="text-center py-12">Chargement...</div> : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom d&apos;utilisateur</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Nom complet</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Rôle</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">État</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Créé le</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = currentUser?.id === u.id;
                  const isBlocked = u.is_blocked;
                  return (
                    <tr key={u.id} className={`border-b border-slate-100 ${isBlocked ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                      <td className={`py-3 px-4 text-sm font-medium ${isBlocked ? 'text-red-700' : 'text-slate-900'}`}>
                        {u.username}
                        {isSelf && <span className="ml-2 text-xs text-blue-600">(vous)</span>}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{u.full_name || '-'}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          u.role === 'admin' ? 'bg-blue-100 text-blue-700' : u.role === 'technician' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {u.role === 'admin' ? 'Administrateur' : u.role === 'technician' ? 'Technicien' : 'Vendeur'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {isBlocked ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                            <Lock size={12} weight="bold" /> Bloqué
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                            <LockOpen size={12} weight="bold" /> Actif
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(u.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openPasswordDialog(u)}
                          data-testid={`change-password-${u.id}`}
                          className="inline-flex p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-1"
                          title="Modifier le mot de passe"
                        >
                          <KeyIcon size={18} />
                        </button>
                        {!isSelf && (
                          <button
                            onClick={() => toggleBlock(u)}
                            data-testid={`toggle-block-${u.id}`}
                            className={`inline-flex p-2 rounded-md mr-1 ${
                              isBlocked
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-orange-600 hover:bg-orange-50'
                            }`}
                            title={isBlocked ? 'Débloquer' : 'Bloquer'}
                          >
                            {isBlocked ? <LockOpen size={18} /> : <Lock size={18} />}
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            onClick={() => handleDelete(u)}
                            data-testid={`delete-user-${u.id}`}
                            className="inline-flex p-2 text-red-700 hover:bg-red-50 rounded-md"
                            title="Supprimer"
                          >
                            <Trash size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create user dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle>Créer un utilisateur</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom d&apos;utilisateur *</label>
                <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} data-testid="user-username-input" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe *</label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} data-testid="user-password-input" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet</label>
                <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} data-testid="user-fullname-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} data-testid="user-role-select" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                  <option value="seller">Vendeur</option>
                  <option value="technician">Technicien</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-user-btn" className="bg-blue-600 hover:bg-blue-500 text-white">Créer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change password dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent data-testid="password-dialog">
            <DialogHeader>
              <DialogTitle>Modifier le mot de passe — {editingUser?.username}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nouveau mot de passe *</label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" required minLength={4} autoFocus />
                <p className="text-xs text-slate-500 mt-1">Minimum 4 caractères</p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>Annuler</Button>
                <Button type="submit" data-testid="submit-password-btn" className="bg-blue-600 hover:bg-blue-500 text-white">Modifier</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Users;
